"use client";

import DavisLogo from "+/davis-construction.webp";
import Image from "next/image";
import Link from "next/link";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/16/solid";
import { useEffect, useState } from "react";
import SolutionsContextMenu from "./impl/solutions.context-menu";
import SolutionsAccordion from "./impl/solutions.accordion";

export default function Header() {
  const [openNav, setOpenNav] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const body = document.body;
    const originalOverflow = body.style.overflow;

    if (openNav) {
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = "";
    }

    return () => {
      body.style.overflow = originalOverflow;
    };
  }, [openNav]);

  useEffect(() => {
    if (!openNav) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenNav(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openNav]);

  const menuWidthClass = openNav
    ? "w-3/4 max-w-xs border-r-2 px-6"
    : "w-0 border-r-0 px-0";

  return (
    <header className="border-b bg-background border-border sticky z-50 top-0 p-4">
      <div className="max-w-screen-2xl mx-auto flex justify-between items-center">
        <Link href="/" className="relative h-12 w-48 flex items-center">
          <Image
            src={DavisLogo}
            alt="Davis Construction Group Logo"
            quality={100}
            fill
            sizes="192px"
            className="object-contain object-left"
            priority
          />
        </Link>
        <div className="hidden lg:flex items-center gap-6 text-neutral-400">
          <SolutionsContextMenu />

          <Link
            href="/contact"
            className="hover:scale-105 transition-transform"
          >
            <span className="py-2 px-6 font-semibold text-background bg-yellow rounded-xl inline-flex items-center justify-center">
              Contact Us
            </span>
          </Link>
        </div>
        <button
          onClick={() => setOpenNav((prevState) => !prevState)}
          className="bg-s-black px-3 py-3 border border-border rounded-full z-40 lg:hidden text-white transition-transform"
          aria-label={openNav ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={openNav}
        >
          {openNav ? <XMarkIcon width={35} /> : <Bars3Icon width={35} />}
        </button>
        {openNav && (
          <button
            type="button"
            aria-hidden="true"
            className="fixed inset-0 bg-black/60 z-20 lg:hidden"
            onClick={() => setOpenNav(false)}
          />
        )}
        <div
          className={`bg-black border-border z-30 fixed top-0 left-0 h-screen overflow-hidden transition-all duration-300 ease-out ${menuWidthClass}`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!openNav}
        >
          <div className="text-2xl h-full flex flex-col items-center justify-center gap-y-5">
            {openNav && <SolutionsAccordion controller={() => setOpenNav(false)} />}

            <Link
              href="/contact"
              onClick={() => setOpenNav(false)}
              className="hover:scale-105 transition-transform"
            >
              <span className="py-2 px-6 whitespace-nowrap font-semibold text-background bg-yellow rounded-xl inline-flex items-center justify-center">
                Contact Us
              </span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
